import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { StreamVideo, StreamCall, StreamTheme, ParticipantView, useCallStateHooks } from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import { ArrowLeft, Mic, MicOff, Video, VideoOff, Radio, Square, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLiveSession } from "@/lib/live";
import { LiveChat } from "@/components/LiveChat";

const ctl = "flex min-h-[44px] items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold";
const sel = "min-h-[44px] w-full rounded-xl border border-white/15 bg-white/5 px-3 text-sm sm:w-auto sm:max-w-[220px]";
const CONN = {
  joined: { label: "Connected", cls: "text-emerald-300" },
  joining: { label: "Connecting…", cls: "text-[#a7a0af]" },
  reconnecting: { label: "Reconnecting…", cls: "text-amber-300" },
  migrating: { label: "Reconnecting…", cls: "text-amber-300" },
  offline: { label: "Offline — check your connection", cls: "text-red-300" },
  "reconnecting-failed": { label: "Connection lost — reload the studio", cls: "text-red-300" },
  left: { label: "Disconnected", cls: "text-red-300" },
};

function DevicePick({ devices, value, onPick, label, testid }) {
  if (devices.length < 2) return null;
  return (
    <select aria-label={label} data-testid={testid} value={value || ""} onChange={(e) => onPick(e.target.value)} className={sel}>
      {devices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${label} ${i + 1}`}</option>)}
    </select>
  );
}

function StudioInner({ s, sessionId }) {
  const navigate = useNavigate();
  const { useLocalParticipant, useCameraState, useMicrophoneState, useIsCallLive, useParticipantCount, useCallCallingState } = useCallStateHooks();
  const me = useLocalParticipant();
  const { isMute: camOff, devices: cams = [], selectedDevice: camId } = useCameraState();
  const { isMute: micOff, devices: mics = [], selectedDevice: micId } = useMicrophoneState();
  const calling = useCallCallingState();
  const conn = CONN[calling] || { label: String(calling || "Connecting"), cls: "text-[#a7a0af]" };
  const live = useIsCallLive();
  const viewers = Math.max(0, useParticipantCount() - 1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const call = s.call;

  const toggle = async (dev) => {
    setErr("");
    try {
      await dev.toggle();
    } catch {
      setErr("Camera or microphone access was blocked. Allow it in your browser settings (on iPhone: Settings › Safari › Camera / Microphone), then try again.");
    }
  };
  const goLive = async () => {
    setBusy(true);
    setErr("");
    try {
      if (camOff) await call.camera.enable();
      if (micOff) await call.microphone.enable();
      await api.post(`/creator/live/sessions/${sessionId}/go-live`);
      toast.success("You're live");
    } catch (e) {
      setErr(e.response ? formatApiErrorDetail(e.response.data?.detail) : "Allow camera and microphone access first, then tap Go Live again.");
    } finally {
      setBusy(false);
    }
  };
  const endLive = async () => {
    if (!window.confirm("End this livestream for everyone?")) return;
    await api.post(`/creator/live/sessions/${sessionId}/end-live`).catch(() => {});
    navigate("/creator/live");
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-3">
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black">
          {me && !camOff ? <ParticipantView participant={me} trackType="videoTrack" mirror /> : (
            <div className="flex h-full items-center justify-center text-sm text-[#a7a0af]">Camera is off — tap the camera button to preview</div>
          )}
          <span data-testid="studio-live-state" className={`absolute left-3 top-3 rounded-md px-2 py-1 text-xs font-bold uppercase ${live ? "bg-[#d42be8]" : "bg-white/15"}`}>{live ? "Live" : "Backstage"}</span>
          <span data-testid="studio-viewer-count" className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-xs"><Users size={12} /> {viewers}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => toggle(call.camera)} data-testid="studio-toggle-camera" className={`${ctl} border-white/15`}>{camOff ? <VideoOff size={16} /> : <Video size={16} />} {camOff ? "Camera off" : "Camera on"}</button>
          <button onClick={() => toggle(call.microphone)} data-testid="studio-toggle-mic" className={`${ctl} border-white/15`}>{micOff ? <MicOff size={16} /> : <Mic size={16} />} {micOff ? "Mic muted" : "Mic on"}</button>
          {!live ? (
            <button disabled={busy} onClick={goLive} data-testid="studio-go-live" className={`${ctl} btn-brand border-transparent`}><Radio size={16} /> {busy ? "Starting…" : "Go Live"}</button>
          ) : (
            <button onClick={endLive} data-testid="studio-end-live" className={`${ctl} border-red-400/40 text-red-300`}><Square size={16} /> End Live</button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <DevicePick devices={cams} value={camId} onPick={(id) => call.camera.select(id)} label="Camera" testid="studio-camera-select" />
          <DevicePick devices={mics} value={micId} onPick={(id) => call.microphone.select(id)} label="Microphone" testid="studio-mic-select" />
        </div>
        <p data-testid="studio-connection" className={`text-xs ${conn.cls}`}>Connection: {conn.label}{live ? " · you are visible in Live Now" : " · backstage, not visible to fans yet"}</p>
        {err && <p data-testid="studio-broadcast-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{err}</p>}
      </div>
      <LiveChat chat={s.chat} channel={s.channel} moderator />
    </div>
  );
}

export default function LiveStudio() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const s = useLiveSession(`/creator/live/sessions/${sessionId}/studio`);
  return (
    <div data-testid="live-studio" className="flex flex-col gap-4">
      <button onClick={() => navigate("/creator/live")} data-testid="studio-back" className="flex items-center gap-1 self-start text-sm text-[#a7a0af]"><ArrowLeft size={16} /> Go Live</button>
      <h1 data-testid="studio-title" className="text-2xl font-extrabold">{s.creds?.session?.title || "Live studio"}</h1>
      {s.status === "connecting" && <p className="text-sm text-[#a7a0af]">Connecting to the live studio…</p>}
      {s.status === "error" && <p data-testid="studio-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{s.error}</p>}
      {s.status === "ready" && (
        <StreamVideo client={s.video}>
          <StreamTheme>
            <StreamCall call={s.call}><StudioInner s={s} sessionId={sessionId} /></StreamCall>
          </StreamTheme>
        </StreamVideo>
      )}
    </div>
  );
}
